import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Webpack lazy loading on Rails with CDN support",
  "description": "",
  "published": "2015-06-04T00:00:00.000Z",
  "redirect_from": ["/blog/webpack-lazy-loading-on-rails-with-cdn-support/swizec/6689"],
  "categories": "CommonJS, ECMAScript, JavaScript, Ruby (programming language), Technical, Web browser",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Webpack is the best module bundler I've ever used. Just this week I used it to reduce the JS footprint of an app from 906KB to 87KB for mobile visitors. An 800KB difference!`}</p>
    <p><a parentName="p" {...{
        "href": "http://webpack.github.io/"
      }}>{`Webpack`}</a>{`'s core premise is that you can `}<inlineCode parentName="p">{`require('./foo')`}</inlineCode>{` your `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`JavaScripts`}</a>{`. That sea of `}<inlineCode parentName="p">{`<script>`}</inlineCode>{` tags or concatenated code and variables injected into the global namespace is gone.`}</p>
    <p>{`In theory we've had that for a while. RequireJS and its AMD have been out for a while, but it's always felt clunky. Don't think I've used it more than once in the wild. Too clunky.`}</p>
    <p><a parentName="p" {...{
        "href": "http://browserify.org/"
      }}>{`Browserify`}</a>{` was better. Using `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/CommonJS",
        "title": "CommonJS"
      }}>{`CommonJS`}</a>{` it brought the same syntax we've had in node.js into the browser. Less clunky and easier to reason about. But to my knowledge, not all that popular in the wild.`}</p>
    <p>{`The problem with Browserify is that, at least in my experience, it takes a lot of setup to work well. And it's kind of slow. Compile times run into the many seconds range.`}</p>
    <p>{`Webpack is fast. Like super fast. Compiling a huge codebase rarely takes more than a second. Every time I change something it's already compiled by the time I refresh the browser.`}</p>
    <p>{`Magic.`}</p>
    <h2 {...{
      "id": "lazy-loading-your-code"
    }}>{`Lazy loading your code`}</h2>
    <p>{`Another great feature of Webpack is that it can perform a sort of minimal bundle coverage for your dependency graph. Instead of making one huge bundle that's got all your code, it makes a few smaller discrete ones.`}</p>
    <p>{`Users no longer have to download all the code for `}<inlineCode parentName="p">{`/admin_panel`}</inlineCode>{` when they're looking at `}<inlineCode parentName="p">{`/user_profile`}</inlineCode>{`. Not only that, Webpack automagically downloads the required javascripts when needed.`}</p>
    <p>{`This is called lazy loading and it is magic.`}</p>
    <p>{`It's easy in theory, whenever you require a dependency like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`require.ensure([], function () {
  var more_code = require("./more_code");
});
`}</code></pre>
    <p>{`Webpack will say `}<em parentName="p">{`"Oop, that's an optional dependency!"`}</em>{`. It will package everything that might get required by that `}<inlineCode parentName="p">{`require`}</inlineCode>{` call into a separate bundle. In this case just `}<inlineCode parentName="p">{`./more_code.js`}</inlineCode>{`.`}</p>
    <p>{`If your path includes a variable, Webpack understands that too and puts everything that could match in a bundle. So with `}<inlineCode parentName="p">{`require('./'+module_name+'View.js')`}</inlineCode>{`, for instance, it will bundle all JavaScript files that end with `}<inlineCode parentName="p">{`View`}</inlineCode>{` into one file.`}</p>
    <p>{`Webpack splits your bundle on every `}<inlineCode parentName="p">{`require.ensure`}</inlineCode>{` call. Then when your code needs something, Webpack makes a jsonp call to get it from your server.`}</p>
    <p>{`This is great in development. Not great in production.`}</p>
    <p>{`In my tests on a Heroku Rails app, doing a HEAD call to our app took some 200ms, while the same call to amazon's CloudFlare took only 10ms.`}</p>
    <p>{`Our app is an order of magnitude slower than a proper static file server. Oops.`}</p>
    <h2 {...{
      "id": "lazy-loading-with-cdn-support"
    }}>{`Lazy Loading with CDN support`}</h2>
    <p>{`First of all, you have to get Webpack working with Rails. The best guide I've found comes from Justin Gordon, `}<a parentName="p" {...{
        "href": "http://www.railsonmaui.com/blog/2014/10/02/integrating-webpack-and-the-es6-transpiler-into-an-existing-rails-project/"
      }}>{`here`}</a>{`. He takes you through the whole process in detail.`}</p>
    <p>{`The nutshell version goes like this:`}</p>
    <ol>
      <li parentName="ol">{`Use npm to install Webpack and its things`}</li>
      <li parentName="ol">{`Configure it in `}<inlineCode parentName="li">{`webpack.rails.config.js`}</inlineCode></li>
      <li parentName="ol">{`Add a call to `}<inlineCode parentName="li">{`webpack -w --config=webpack.rails.config.js`}</inlineCode>{` to your startup sequence (I like using `}<inlineCode parentName="li">{`grunt start`}</inlineCode>{` of some sort)`}</li>
      <li parentName="ol">{`Remove all `}<inlineCode parentName="li">{`//= require`}</inlineCode>{` in `}<inlineCode parentName="li">{`application.js`}</inlineCode>{` and replace with `}<inlineCode parentName="li">{`//= require generated/rails-bundle.js`}</inlineCode></li>
      <li parentName="ol">{`Make an assets.rake task that looks like `}<a parentName="li" {...{
          "href": "https://gist.github.com/Swizec/fb0905ba078520117c17"
        }}>{`this gist`}</a></li>
    </ol>
    <p>{`You can find the details in Gordon's long blogpost. It's really good. Although personally I didn't go as far as moving all my JavaScripts out of `}<inlineCode parentName="p">{`app/assets`}</inlineCode>{` and I didn't set up hot code loading for local dev.`}</p>
    <p>{`Now, to make Webpack understand that static assets should be loaded from a CloudFlare CDN, you have to change its `}<inlineCode parentName="p">{`output`}</inlineCode>{` settings.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`config.output = {
  filename: in_dev() ? "rails-bundle.js" : "[id].[chunkhash].rails-bundle.js",
  path: "app/assets/javascripts/generated",
  publicPath: getCDN() + "/assets/generated/",
};
`}</code></pre>
    <p>{`There's two tricks here:`}</p>
    <ol>
      <li parentName="ol">{`We tell Webpack to fingerprint files with `}<inlineCode parentName="li">{`[chunkhash]`}</inlineCode>{` unless we're in local development. This gives us long-term caching because of unique filenames`}</li>
      <li parentName="ol">{`We prefix the `}<inlineCode parentName="li">{`publicPath`}</inlineCode>{` with a CDN URL. This "tricks" Webpack into loading them from there`}</li>
    </ol>
    <p>{`The `}<inlineCode parentName="p">{`in_dev`}</inlineCode>{` and `}<inlineCode parentName="p">{`getCDN`}</inlineCode>{` make the config easier to understand. They look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function getCDN() {
  var CDNs = {
    staging: ".cloudfront.net",
    production: ".cloudfront.net",
    preproduction: ".cloudfront.net",
  };

  if (!in_dev()) {
    return "//" + CDNs[process.env.RAILS_ENV.toLowerCase()];
  }

  return "";
}

function in_dev() {
  return !process.env.RAILS_ENV || process.env.RAILS_ENV == "development";
}
`}</code></pre>
    <p>{`They both look at the `}<inlineCode parentName="p">{`RAILS_ENV`}</inlineCode>{` environment variable to decide which situation Webpack is running under. It works great, but does mean that we have two places to set the config for CDN. Here and the regular Ruby config.`}</p>
    <p>{`Now, because our files are fingerprinted, Rails's asset pipeline no longer knows how to insert them. The filename `}<inlineCode parentName="p">{`0.some_weird_hash.rails-bundle.js`}</inlineCode>{` changes every time Webpack compiles our code.`}</p>
    <p>{`The simplest fix I've found is to copy the file in that `}<inlineCode parentName="p">{`assets.rake`}</inlineCode>{` task. Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`FileUtils.copy_file(Pathname.glob(path+'0.*.rails-bundle.js').first,
                        File.join(path, 'rails-bundle.js'))
`}</code></pre>
    <p>{`Now the main bundle file will always have the same name in production and `}<inlineCode parentName="p">{`application.js`}</inlineCode>{` will be able to include it so you don't have to hack the asset pipeline. Perfect.`}</p>
    <h2 {...{
      "id": "benefits"
    }}>{`Benefits`}</h2>
    <p>{`There are many reasons you'd want to use Webpack in your Rails application. My main motivation was that our users were just loading way too much JavaScript. They don't need the desktop homepage app when they're on mobile. And they definitely don't need the whole user dashboard when they're looking at the homepage.`}</p>
    <p>{`For mobile users the change has been significant. From downloading all of `}<strong parentName="p">{`906KB`}</strong>{` before, to just `}<strong parentName="p">{`87KB`}</strong>{`. Sure, there's still some overhead on top of that for libraries, but we can load those from 3rd party CDNs now so they could already be cached.`}</p>
    <p>{`The other huge benefit is that our code is easier to deal with. Instead of shoving everything into a huge carefully namespaced global object, we can just `}<inlineCode parentName="p">{`require`}</inlineCode>{` what we need.`}</p>
    <p>{`Life is better.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      